import {
  DEFAULT_LOCALE, LOCALE_TAGS, type Locale, type MessageKey, type Vars, isLocale, pickLocale, t as translate, tn as translateN,
} from '../../src/i18n/index';

// ------------------------------------------------------------------ language
let current: Locale = DEFAULT_LOCALE;
const STORE = 'ui-lang';

export const getLocale = (): Locale => current;
export function setLocale(l: Locale, remember = true) {
  current = l;
  document.documentElement.lang = l;
  if (remember)
    try {
      localStorage.setItem(STORE, l);
    } catch {
      /* private mode: the choice just will not stick */
    }
}
/** Translate into the current language. */
export const T = (key: MessageKey | (string & {}), vars?: Vars) => translate(current, key, vars);
export const TN = (key: string, n: number, vars?: Vars) => translateN(current, key, n, vars);

/**
 * Pick the interface language: an explicit ?lang=, then the person's earlier
 * choice, then their browser, then the restaurant's default.
 */
export function chooseLocale(offered: string[] | null, restaurantDefault: string | null): Locale {
  const allowed = (offered ?? []).filter(isLocale);
  const ok = (l: Locale) => allowed.length === 0 || allowed.includes(l);
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORE);
  } catch {
    /* ignore */
  }
  const fromUrl = new URLSearchParams(location.search).get('lang');
  for (const c of [fromUrl, stored, ...(navigator.languages ?? [navigator.language])]) {
    const l = pickLocale([c], null as unknown as Locale);
    if (c && isLocale(l) && ok(l)) return l;
  }
  const d = pickLocale([restaurantDefault]);
  return ok(d) ? d : (allowed[0] ?? DEFAULT_LOCALE);
}

export interface ApiError extends Error {
  code: string;
  status: number;
  details: any;
}

export async function api<T = any>(
  method: string,
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(headers ?? {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    /* empty body */
  }
  // A signed-in call that comes back 401 means the session ended.
  if (res.status === 401 && !path.startsWith('/v1/auth/login') && !path.startsWith('/v1/auth/me')) {
    window.dispatchEvent(new CustomEvent('auth:expired'));
  }
  if (!res.ok) {
    const e = new Error(json?.error?.message ?? `Request failed (${res.status})`) as ApiError;
    e.code = json?.error?.code ?? 'error';
    e.status = res.status;
    e.details = json?.error?.details ?? null;
    throw e;
  }
  return json as T;
}

export const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export function fmtTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat(LOCALE_TAGS[current], { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date(iso));
}

export function fmtDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(LOCALE_TAGS[current], { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

export function addDaysISO(isoDate: string, n: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

export function todayIn(tz: string): string {
  // Built from parts so the result is YYYY-MM-DD in every locale and browser.
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date())
    .reduce<Record<string, string>>((acc, p) => ((acc[p.type] = p.value), acc), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function minutesToLabel(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

export function hhmm(mins: number): string {
  return `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

export function labelHhmm(h: string): string {
  const [hh, mm] = h.split(':').map(Number);
  if (current === 'de') return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  const ap = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${ap}`;
}

/** A readable message for a failed request: our translation of its code, else the server text. */
export function errorText(e: unknown): string {
  const err = e as { code?: string; message?: string };
  const key = `err.${err?.code ?? ''}`;
  const known = err?.code && translate(current, key) !== key;
  if (known) return translate(current, key);
  if (err?.code === 'error' || !err?.message) return translate(current, 'err.unknown');
  return err.message;
}

/** Long date and time for a moment in a time zone, in the current language. */
export function whenLabel(iso: string, tz: string): string {
  return translate(current, 'when.format', {
    date: new Intl.DateTimeFormat(LOCALE_TAGS[current], { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz }).format(new Date(iso)),
    time: new Intl.DateTimeFormat(LOCALE_TAGS[current], { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date(iso)),
  });
}

/** YYYY-MM-DD of an instant in a time zone, independent of the browser locale. */
export function dateIn(iso: string | Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(typeof iso === 'string' ? new Date(iso) : iso)
    .reduce<Record<string, string>>((acc, p) => ((acc[p.type] = p.value), acc), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}
